import { z } from "zod";
import type { PrinterGroupId, PrinterPlace } from "./printers-api";

/**
 * Grupy urządzeń bez apteki: polska wartość `?grupa=` ↔ `group` z API. Jedyne
 * miejsce tego mapowania — reszta modułu pracuje na `PrinterPlace`.
 */
const GROUP_SLUGS = ["laptopy-biurowe", "natubay"] as const;
type GroupSlug = (typeof GROUP_SLUGS)[number];
const GROUP_BY_SLUG: Record<GroupSlug, PrinterGroupId> = {
	"laptopy-biurowe": "office_laptop",
	natubay: "natubay",
};
const SLUG_BY_GROUP = Object.fromEntries(
	GROUP_SLUGS.map((slug) => [GROUP_BY_SLUG[slug], slug]),
) as Record<PrinterGroupId, GroupSlug>;

/** Search params `/konfiguracja/drukarki` — każde pole z `.catch`, zły link nie wywraca trasy. */
export const printersSearchSchema = z.object({
	/** idfirm wybranej apteki. */
	apteka: z.coerce.number().int().positive().optional().catch(undefined),
	/** Wybrana grupa urządzeń bez apteki (zamiast `apteka`). */
	grupa: z.enum(GROUP_SLUGS).optional().catch(undefined),
	tab: z.enum(["drukarki", "historia"]).default("drukarki").catch("drukarki"),
	/** Filtry kolumn tabeli historii (`[{ id, value }]`). */
	filtry: z
		.array(z.object({ id: z.string(), value: z.unknown() }))
		.optional()
		.catch(undefined),
});

export type PrintersSearch = z.infer<typeof printersSearchSchema>;
export type PrintersTab = PrintersSearch["tab"];

export function groupSlug(group: PrinterGroupId): GroupSlug {
	return SLUG_BY_GROUP[group];
}

/**
 * Wybrane miejsce z adresu. Panel przy wyborze czyści drugi parametr, więc oba
 * naraz daje tylko ręcznie sklejony link — wtedy wygrywa apteka (starszy,
 * powszechniejszy parametr; grupy widzi wyłącznie super_admin).
 */
export function placeFromSearch(
	search: Pick<PrintersSearch, "apteka" | "grupa">,
): PrinterPlace | null {
	if (search.apteka != null) return { kind: "pharmacy", idfirm: search.apteka };
	if (search.grupa) return { kind: "group", group: GROUP_BY_SLUG[search.grupa] };
	return null;
}

/** Parametry adresu dla miejsca — zawsze czyści drugi rodzaj wyboru. */
export function placeSearch(place: PrinterPlace): Pick<PrintersSearch, "apteka" | "grupa"> {
	return place.kind === "pharmacy"
		? { apteka: place.idfirm, grupa: undefined }
		: { apteka: undefined, grupa: groupSlug(place.group) };
}
